import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { DisclaimerDialogComponent } from 'src/app/dialogs/disclaimer-dialog/disclaimer-dialog.component';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { FisheryNoticesService } from 'src/app/services/storage/fishery-notices.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { Whirlpool, encoders } from 'whirlpool-hash';
import { UserData } from '../../data-models/user-models/user-data.model';
import { generalValidator } from '../../data-models/validator.model';
import { AuthenticationService } from '../../services/authentication.service';
import { ValidationService } from '../../services/validation.service';
import { Tel, TelInputComponent } from '../../tel-input/tel-input.component';



@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.css'],
    standalone: false
})
export class SignUpComponent implements OnInit {

  @ViewChild('telInput') telInput!: TelInputComponent;

  // get the current user details
  userDetails: UserData = new UserData();

  // show error message when field is invalid
  emailError: string = 'Required';
  passwordError: string = 'Required';
  postalError: string = 'Required';

  // phone validation
  userPhone: Tel = new Tel();
  phoneValid: boolean = false;

  passwordHide: boolean = true;
  confirmPasswordHide: boolean = true;

  //When waiting on requests
  sending: boolean = false;

  // regular expression for Canadian postal code validation
  // from https://stackoverflow.com/questions/15774555/efficient-regex-for-canadian-postal-code-function
  postalRegex: RegExp = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

  // regular expression for email address validation
  // this regex allows certain special characters (% . _ - and +) but not consecutive or at the beginning or end of the address.
  // domains must contain only letters, numbers, and periods (.) or hyphens(-), no other special characters
  emailRegex: RegExp = /^(?!.*[%._\-\+]{2,})[A-Z0-9]+(?:[%._\-\+][A-Z0-9]+)*@[A-Z0-9]+(?:-[a-zA-Z0-9]+)*(\.[A-Z0-9]+(?:-[A-Z0-9]+)*)+$/i;

  //Exact requirements for passwords can be set to whatever but for now, require length >=8 but less than 51
  signupForm: UntypedFormGroup = new UntypedFormGroup({
    firstName: new FormControl<string>('', [Validators.required]),
    lastName: new FormControl<string>('', [Validators.required]),
    fin: new FormControl<string>('', [Validators.required]),
    password: new FormControl<string>('', [Validators.required, Validators.minLength(8), Validators.maxLength(50)]),
    confirmPassword: new FormControl<string>('', [Validators.required, Validators.minLength(8), Validators.maxLength(50)]),
    userEmail: new FormControl<string>('', [Validators.required, Validators.pattern(this.emailRegex)]),
    address: new FormControl<string>('', [Validators.required]),
    city: new FormControl<string>('', [Validators.required]),
    postalCode: new FormControl<string>('', [Validators.required, Validators.pattern(this.postalRegex)]),
    phoneNum: new FormControl<Tel>(new Tel(), [Validators.required, generalValidator()])},
    { validators: [ValidationService.match('password', 'confirmPassword')] }
  );

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private onlineStatusService: OnlineStatusService,
    private authenticationService: AuthenticationService,
    public sharedMethodsService: SharedMethodsService
  ) {

  }


  /*  ---------- Initialization ---------- */


  ngOnInit(): void {

    this.validatePostalCode();
    this.validateEmail();
    this.validatePassword();

  }


  /*  ---------- Validation Methods ---------- */


  // consider moving to validation service:
  validateEmail(): void {

    let userEmail: AbstractControl<any, any> | null = this.signupForm.get('userEmail');

    // contact email validation
    userEmail?.valueChanges.subscribe(() => {
      let userEmailErrors: ValidationErrors | null | undefined = userEmail?.errors;
      if (userEmailErrors?.required) {
        this.emailError = 'Required';
      }
      else if (userEmailErrors?.pattern) {
        this.emailError = 'Invalid format. Email addresses may only contain letters, numbers, periods, underscores, and hyphens';
      }
      else {
        this.emailError = '';
      }
    });

  }

  validatePassword(): void {

    let password: AbstractControl<any, any> | null = this.signupForm.get('password');

    password?.valueChanges.subscribe(() => {
      this.signupForm.controls.confirmPassword.updateValueAndValidity();
      let passwordErrors: ValidationErrors | null | undefined = password?.errors;
      if (passwordErrors?.required) {
        this.passwordError = 'Required';
      }
      else if (passwordErrors?.minlength) {
        this.passwordError = 'Minimum length 8 required';
      }
      else if (passwordErrors?.maxlength) {
        this.passwordError = 'Maximum length 50 exceeded';
      }
      else {
        this.passwordError = '';
      }
    });

  }

  // consider moving to validation service:
  validatePostalCode(): void {

    let postalCode: AbstractControl<any, any> | null = this.signupForm.get('postalCode');

    // postal code validation
    postalCode?.valueChanges.subscribe(() => {
      let postalCodeErrors: ValidationErrors | null | undefined = postalCode?.errors;
      if (postalCodeErrors?.required) {
        this.postalError = 'Required';
      }
      else if (postalCodeErrors?.pattern) {
        this.postalError = 'Invalid format';
      }
      else {
        this.postalError = '';
      }
    });

  }

  // validate phone number
  validatePhoneNum(): void {

    this.telInput.touched = true;
    this.telInput.stateChanges.next();
    this.signupForm.get('phoneNum')?.updateValueAndValidity();
    this.userPhone = Tel.transformAnonymous(this.signupForm.get('phoneNum')?.value);
    this.phoneValid = this.userPhone.isValid();

  }

  // validates disclaimer agreement
  openDisclaimerDialog(): void {
    if (this.signupForm.invalid) {
      this.sharedMethodsService.toastSignupMissingInfoError();
      return;
    }

    const dialogRef = this.dialog.open(DisclaimerDialogComponent, {
      width: '90%',
      height: '90%'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.attemptSignUp();
      }
    });

  }
  
  /*  ---------- User Data Methods ---------- */


  // populate user details and put it into PouchDB
  saveUserDetails(user: UserData): void {

    UserStorageService.getUserOnline(user.userEmail, user).then(() => {
      this.userDetails = UserStorageService.currUser;
    }).then(() => {
      this.sharedMethodsService.openSkipPrivacyConfirmationDialog().subscribe(disable => {
          user.hasDisabledPrivacyNotice = disable ?? false;
          UserStorageService.saveUser(user, true).then(() => {
            CommonDataService.pullCommonData().then((result: boolean) => { 
              if (result) {
                this.sharedMethodsService.toastSignUpComplete();
                AuthenticationService.setLogin(user.userEmail);
                FisheryNoticesService.checkForNotices(user.userEmail); // check for new notices (can do this as a background process)
                
                this.router.navigate([user.currHomepage]);
              } else {
                this.sending = false;
                this.sharedMethodsService.toastDataSyncError();
                return;
              }
            }); 
          });
      });
    });

  }

  setUpNewUser(): UserData {

    let newUser: UserData = UserData.transformAnonymous(this.signupForm.value);
    newUser.currHomepage = 'no-trip-home';
    newUser.userEmail = newUser.userEmail.toLowerCase();
    newUser.passwordHash = encoders.toBase64(new Whirlpool().getHash(this.signupForm.get('password')?.value).toString());
    return newUser;

  }


  /*  ---------- Sign Up Methods ---------- */


  // attempt to sign up for an account
  attemptSignUp(): void { // should probably separate code into smaller methods

    if (this.onlineStatusService.getStatus() === OnlineStatusType.OFFLINE) {
      this.sharedMethodsService.toastOfflineError();
      return;
    }

    // validate entered phone number
    this.validatePhoneNum();

    // check signup entry validity
    if (this.signupForm.invalid || !this.phoneValid) {
      this.sharedMethodsService.toastSignupMissingInfoError();
      return;
    }

    this.sending = true;
    let newUser: UserData = this.setUpNewUser();

    this.sharedMethodsService.openNoticeDialog().subscribe(response => {
      if (response) {
        newUser.passedPrivacyNotice = true;

        this.authenticationService.postSignUp(newUser, this.signupForm.get('password')?.value).subscribe((serverResponse: any) => {
          // save entered user fields into PouchDB, then navigate to the home screen
          newUser.apiKey = serverResponse.user.apiKey;
          newUser.logbooks = serverResponse.user.logbooks;
          this.saveUserDetails(newUser);
        }, (err: any) => {
          console.log(err);
          this.sending = false;
          if (err.error.reason == 'Document update conflict.') this.sharedMethodsService.toastSignupEmailInUseError();
          else this.sharedMethodsService.toastSignupCouldNotCompleteError();
        });  
      } else {
        this.sending = false;
      }
    });
    
  }


  /*  ---------- Misc Methods ---------- */


  // go back to the login screen
  backToLogin(): void {

    this.router.navigate(['login']);

  } 

}
